/**
 * MUI DistanceInput Component
 * 
 * This component is the Material UI equivalent of the original DistanceInput component.
 * It provides an input field for distance values with unit conversion.
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReducersIndex from 'reduxModules/ducks';
import Input from '../atoms/Input';
import { toFeet, toMeter, distanceToString, distanceToNumber } from 'utilities/format';
import { getCurrentValue2 } from 'reduxModules/selectors/containerSelectors';
import { round, get } from 'lodash';
import { micInCategory } from 'types/models/microphone';
import { DistanceInputProps } from 'types/components/molecules';
import { AnyAction } from '@reduxjs/toolkit';
import { IconButton, InputAdornment, Box } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

/**
 * MUI DistanceInput component
 * 
 * @param props - Component props
 * @returns The MUI DistanceInput component
 */
const MuiDistanceInput: React.FC<DistanceInputProps> = (props) => {
  // Helper function to safely call getCurrentValue2 with TypeScript
  const safeGetValue = (state: any, param: string) => {
    // Cast the function to allow any number of arguments
    const getValue = getCurrentValue2 as any;
    return getValue(state, param);
  };

  // Get units directly from state to ensure we're subscribed to changes
  const units = useSelector((state: any) => state.ui.units) as 'feet' | 'meters';
  const isMeter = units === 'meters';
  
  // Only select from Redux if dispatcher is provided.
  // getStoreProp strips the 'set' prefix, so 'setMicrophones' resolves to area.microphones
  // and 'setExpansionMicrophones' to area.expansionMicrophones — i.e. this is always the
  // mic (or speaker, table, etc.) that THIS field edits, which is what the pole-mount check
  // below depends on.
  const currentValue = useSelector((state: any) =>
    props.dispatcher ? safeGetValue(state, props.dispatcher) : null
  );

  // Pole-mounted mics (TCM-X, DCM-X, CBC 2500 PM) hang in whole 0.5' pole sections, and both
  // setMicrophones and setExpansionMicrophones snap a manually-set height to that grid. The
  // spinner step MUST match the grid: a 0.25' step is exactly half a section, so Math.round()
  // in the reducer returns it to where it started and the DOWN arrow appears completely dead
  // (up still moves, because .5 rounds away from zero). Read the model off `currentValue`
  // rather than a hardcoded 'microphones.model' path — the expansion height field's primary
  // mic is always a bar, so a primary-mic lookup can never identify it.
  const isPoleMountHeight =
    props.name === 'height' &&
    (props.dispatcher === 'setMicrophones' || props.dispatcher === 'setExpansionMicrophones') &&
    micInCategory(get(currentValue, 'model'), 'POLE_MOUNT');

  const spinStep = isPoleMountHeight
    ? (isMeter ? 0.1524 : 0.5)  // One pole section
    : (isMeter ? 0.1 : 0.25);   // Regular increments

  const rounder = isMeter ? 4 : 2; // Higher precision for meters to handle pole snapping

  // Subscribe to error state to detect when warning modals are closed
  const errorState = useSelector((state: any) => state.ui.errorState || false);
  const modalResetKey = useSelector((state: any) => state.ui.modalResetKey || 0);
  
  // Use number for value state, but allow it to be null when empty
  const [value, setValue] = useState<number | null>(null); // numerical value in display units (meters or feet)
  const [valueString, setValueString] = useState<string>('');

  const dispatch = useDispatch();
  
  // Update local state from Redux and when units change
  useEffect(() => {
    if (currentValue) {
      const currentMeterValue = get(currentValue, props.name);

      // Always store value in current display units (meters or feet)
      const displayValue = isMeter ? currentMeterValue : toFeet(currentMeterValue, true);
      const formattedString = distanceToString(currentMeterValue, isMeter);

      setValue(displayValue);
      setValueString(formattedString);
    }
  }, [currentValue, units, props.name, isMeter]);

  // Force re-initialization when modalResetKey changes
  useEffect(() => {
    if (modalResetKey > 0 && currentValue) {
      const currentMeterValue = get(currentValue, props.name);
      // Always store value in current display units (meters or feet)
      const displayValue = isMeter ? currentMeterValue : toFeet(currentMeterValue, true);
      setValue(displayValue);
      setValueString(distanceToString(currentMeterValue, isMeter));
    }
  }, [modalResetKey, currentValue, isMeter, props.name]);

  // Reset local state when error state clears
  useEffect(() => {
    if (!errorState && currentValue) {
      const currentMeterValue = get(currentValue, props.name);
      const displayValue = isMeter ? currentMeterValue : toFeet(currentMeterValue, true);
      setValue(displayValue);
      setValueString(distanceToString(currentMeterValue, isMeter));
    }
  }, [errorState, currentValue, isMeter, props.name]);

  /**
   * Update states and dispatch to Redux
   */
  const updateStates = (newValue: number, source: 'user' | 'reset') => {
    if (isFinite(newValue) && newValue >= 0) {
      // Update local state
      setValue(newValue);
      const meterValue = isMeter ? newValue : toMeter(newValue, false);
      const formattedString = distanceToString(meterValue, isMeter);
      setValueString(formattedString);
      
      // Dispatch to Redux if this is a user action
      if (source === 'user' && props.dispatcher) {
        dispatch(
          ReducersIndex.container[props.dispatcher]({ [props.name]: meterValue }) as unknown as AnyAction
        );
      }
    }
  };

  /**
   * Handle key down event
   * 
   * @param evt - The key down event
   */
  const handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>): void => {
    const key = evt.key;
    if (!['Delete', 'Backspace', 'Tab'].includes(key) && !(isMeter ? /^[0-9.]+$/ : /^[0-9 '"-.]+$/).test(key)) {
      evt.preventDefault();
    }
    
    const currentInputValue = (evt.target as HTMLInputElement).value;
    
    switch (key) {
      case 'Enter':
      case 'Tab':
        const parsedValue = distanceToNumber(currentInputValue, isMeter);
        updateStates(parsedValue, 'user');
        break;
      case 'ArrowUp':
        evt.preventDefault();
        handleIncrement();
        break;
      case 'ArrowDown':
        evt.preventDefault();
        handleDecrement();
        break;
      default:
        break;
    }
  };

  /**
   * Handle blur event
   * 
   * @param evt - The blur event
   */
  const handleBlur = (evt: React.FocusEvent<HTMLInputElement>): void => {
    const parsedValue = distanceToNumber(evt.target.value, isMeter);
    updateStates(parsedValue, 'user');
  };

  /**
   * Handle change event
   * 
   * @param evt - The change event
   */
  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = evt.target.value;
    setValueString(inputValue);
  };

  /**
   * Handle increment button click
   */
  const handleIncrement = (): void => {
    // Parse the current display string to get the actual current value
    const currentDisplayValue = distanceToNumber(valueString, isMeter);
    const newValue = round(currentDisplayValue + spinStep, rounder);


    // Update local state immediately for responsive UI
    setValue(newValue);
    const meterValue = isMeter ? newValue : toMeter(newValue, false);
    setValueString(distanceToString(meterValue, isMeter));


    // Then dispatch to Redux
    if (props.dispatcher) {
      dispatch(
        ReducersIndex.container[props.dispatcher]({ [props.name]: meterValue }) as unknown as AnyAction
      );
    }
  };

  /**
   * Handle decrement button click
   */
  const handleDecrement = (): void => {
    // Parse the current display string to get the actual current value
    const currentDisplayValue = distanceToNumber(valueString, isMeter);
    const newValue = Math.max(0, round(currentDisplayValue - spinStep, rounder));


    // Update local state immediately for responsive UI
    setValue(newValue);
    const meterValue = isMeter ? newValue : toMeter(newValue, false);
    setValueString(distanceToString(meterValue, isMeter));


    // Then dispatch to Redux
    if (props.dispatcher) {
      dispatch(
        ReducersIndex.container[props.dispatcher]({ [props.name]: meterValue }) as unknown as AnyAction
      );
    }
  };

  /**
   * Handle focus event - select all text for easy editing
   * 
   * @param evt - The focus event
   */
  const handleFocus = (evt: React.FocusEvent<HTMLInputElement>): void => {
    evt.target.select();
  };

  // Return the Input component with increment/decrement buttons
  return (
    <Input
      name={props.name}
      value={valueString}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={props.placeholder ? (typeof props.placeholder === 'boolean' ? '' : props.placeholder) : ''}
      fullWidth
      size="small"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              marginRight: '2px',
              height: '28px',
              justifyContent: 'space-between',
              opacity: 0,
              transition: 'opacity 0.2s ease',
              '.MuiInputBase-root:hover &': {
                opacity: 1,
              },
            }}>
              <IconButton
                size="small"
                onClick={handleIncrement}
                tabIndex={-1}
                sx={{ 
                  padding: '0px',
                  minWidth: '16px',
                  width: '16px',
                  height: '14px',
                  borderRadius: '2px',
                  '& svg': {
                    fontSize: '18px',
                    color: 'text.secondary',
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <ArrowDropUpIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleDecrement}
                tabIndex={-1}
                sx={{ 
                  padding: '0px',
                  minWidth: '16px',
                  width: '16px',
                  height: '14px',
                  borderRadius: '2px',
                  '& svg': {
                    fontSize: '18px',
                    color: 'text.secondary',
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            </Box>
          </InputAdornment>
        ),
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          height: '36px',
          paddingRight: '4px',
        },
        '& input': {
          fontSize: '14px',
          padding: '8px 12px',
        }
      }}
    />
  );
};

export type { DistanceInputProps };
export default MuiDistanceInput;
